import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import socket from "./Soclet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOut } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

const UserList = ({ user, selectedUser, onSelectUser }) => {
  const [users, setUsers] = useState([]);

  //  useCallback to avoid re-creating function every render
  const fetchUsers = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(
        `https://bpohasil.ngrok.app/api/chat/with-last-message/admins/${user.id}`
      );
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching user list:", err);
    }
  }, [user?.id]);

  //  Fetch users when user.id changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  //  Listen for socket messages
  useEffect(() => {
    const handleReceiveMessage = (msg) => {
      if (msg.senderId === user.id || msg.receiverId === user.id) {
        const otherUserId =
          msg.senderId === user.id ? msg.receiverId : msg.senderId;

        // If we’re chatting with this user, mark as read instantly
        if (selectedUser?.id === otherUserId) {
          socket.emit("markAsRead", {
            userId: user.id,
            otherUserId,
          });
          fetchUsers(); // Refresh user list
          return;
        }

        // Move sender to top + increase unread count
        setUsers((prev) => {
          const existing = prev.find((u) => u.id === otherUserId);
          const updatedUser = {
            ...existing,
            id: otherUserId,
            lastMessage: msg.message || "📎 File",
            lastMessageTime: msg.createdAt,
            unreadCount:
              msg.receiverId === user.id
                ? (existing?.unreadCount || 0) + 1
                : existing?.unreadCount || 0,
          };

          const filtered = prev.filter((u) => u.id !== otherUserId);
          return [updatedUser, ...filtered];
        });
      }
    };

    socket.on("receiveMessage", handleReceiveMessage);

    return () => socket.off("receiveMessage", handleReceiveMessage);
  }, [user.id, selectedUser, fetchUsers]);

  //  Handle user click
  const handleSelectUser = (u) => {
    // Reset unread count in UI
    setUsers((prev) =>
      prev.map((usr) =>
        usr.id === u.id ? { ...usr, unreadCount: 0 } : usr
      )
    );

    // Inform backend to clear unread count
    socket.emit("markAsRead", {
      userId: user.id,
      otherUserId: u.id,
    });

    onSelectUser(u);
  };

  return (
    <div className="w-full h-screen bg-white border-r border-gray-300 shadow-lg flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-5 py-4 font-semibold text-lg flex justify-between items-center">
        Chats
        <Link to="/">
          <FontAwesomeIcon icon={faSignOut} className="cursor-pointer" />
        </Link>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {users.length === 0 && (
          <div className="text-gray-500 text-center py-10">
            No users found
          </div>
        )}

        {users.map((u) => (
          <div
            key={u.id}
            onClick={() => handleSelectUser(u)}
            className={`flex items-center justify-between px-5 py-3 border-b border-gray-200 cursor-pointer transition ${
              selectedUser?.id === u.id
                ? "bg-blue-100"
                : "hover:bg-gray-100"
            }`}
          >
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900">
                {u.name}{" "}
                <span className="text-gray-700">({u.role})</span>
              </span>
              <span className="text-sm text-gray-500">{u.email}</span>
              <span className="text-sm text-gray-600 truncate w-[180px]">
                {u.lastMessage}
              </span>
            </div>

            <div className="flex flex-col items-end">
              {u.unreadCount > 0 && (
                <span className="bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                  {u.unreadCount}
                </span>
              )}
              {u.lastMessageTime && (
                <span className="text-xs text-gray-400 mt-1">
                  {new Date(u.lastMessageTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserList;
